import { useState } from "react";
import { Link } from "react-router-dom";
import { useAdminUsers, useBulkGiftScans } from "../../hooks/use-admin.js";
import { Skeleton } from "../../components/skeleton.js";

export function AdminUsersPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const { data, isLoading } = useAdminUsers({
    page,
    limit: 50,
    search: search || undefined,
  });

  // Bulk selection state
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showBulkGift, setShowBulkGift] = useState(false);
  const [bulkAmount, setBulkAmount] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const bulkGift = useBulkGiftScans();

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (!data) return;
    const allIds = data.users.map((u) => u.id);
    const allSelected = allIds.every((id) => selected.has(id));
    if (allSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        allIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        allIds.forEach((id) => next.add(id));
        return next;
      });
    }
  }

  async function handleBulkGift() {
    const amt = parseInt(bulkAmount, 10);
    if (!amt || amt < 1 || amt > 50 || !bulkReason.trim()) return;
    try {
      await bulkGift.mutateAsync({
        userIds: [...selected],
        amount: amt,
        reason: bulkReason.trim(),
      });
      setSelected(new Set());
      setShowBulkGift(false);
      setBulkAmount("");
      setBulkReason("");
    } catch {
      // error shown via bulkGift.isError
    }
  }

  const pageUserIds = data?.users.map((u) => u.id) ?? [];
  const allPageSelected =
    pageUserIds.length > 0 && pageUserIds.every((id) => selected.has(id));

  return (
    <div className="product-page">
      <h1 className="page-title">Users</h1>
      <section className="product-section">
        <input
          type="search"
          placeholder="Search by email\u2026"
          aria-label="Search users by email"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="form-input mb-3"
          style={{ maxWidth: "300px" }}
        />

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div
            className="d-flex gap-2 items-center mb-3"
            style={{
              padding: "0.625rem 1rem",
              background: "var(--color-info-bg)",
              borderRadius: "var(--radius-md)",
            }}
          >
            <span className="fw-600 text-sm">
              {selected.size} selected
            </span>
            {!showBulkGift ? (
              <>
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={() => setShowBulkGift(true)}
                >
                  Gift scans
                </button>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => setSelected(new Set())}
                >
                  Clear
                </button>
              </>
            ) : (
              <div className="d-flex gap-2 items-center">
                <input
                  type="number"
                  className="form-input"
                  min="1"
                  max="50"
                  placeholder="Amount (1-50)"
                  value={bulkAmount}
                  onChange={(e) => setBulkAmount(e.target.value)}
                  style={{ width: "120px" }}
                />
                <input
                  type="text"
                  className="form-input"
                  placeholder="Reason (required)"
                  value={bulkReason}
                  onChange={(e) => setBulkReason(e.target.value)}
                  style={{ width: "240px" }}
                />
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  disabled={
                    bulkGift.isPending || !bulkAmount || !bulkReason.trim()
                  }
                  onClick={() => void handleBulkGift()}
                >
                  {bulkGift.isPending ? "Gifting\u2026" : "Confirm"}
                </button>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => setShowBulkGift(false)}
                >
                  Cancel
                </button>
              </div>
            )}
            {bulkGift.isSuccess && (
              <span className="text-muted text-sm">Done!</span>
            )}
            {bulkGift.isError && (
              <span className="text-sm" style={{ color: "var(--color-danger)" }}>
                {bulkGift.error instanceof Error
                  ? bulkGift.error.message
                  : "Failed"}
              </span>
            )}
          </div>
        )}

        {isLoading && <Skeleton lines={3} />}
        {data && (
          <>
            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={toggleSelectAll}
                        aria-label="Select all users on page"
                      />
                    </th>
                    <th>Email</th>
                    <th>Created</th>
                    <th>Last login</th>
                    <th className="text-center">Admin</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((user) => (
                    <tr key={user.id}>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={selected.has(user.id)}
                          onChange={() => toggleSelect(user.id)}
                          aria-label={`Select ${user.email}`}
                        />
                      </td>
                      <td>{user.email}</td>
                      <td className="text-sm text-muted">
                        {new Date(user.createdAt).toLocaleDateString("en-IE")}
                      </td>
                      <td className="text-sm text-muted">
                        {user.lastLoginAt
                          ? new Date(user.lastLoginAt).toLocaleDateString(
                              "en-IE",
                            )
                          : "\u2014"}
                      </td>
                      <td className="text-center">
                        {user.isAdmin ? "\u2713" : ""}
                      </td>
                      <td>
                        <Link
                          to={`/admin/users/${user.id}`}
                          className="btn btn--secondary btn--sm"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.users.length === 0 && (
              <p
                className="text-muted text-center"
                style={{ padding: "24px" }}
              >
                No users found.
              </p>
            )}
            <div className="d-flex gap-2 items-center mt-3">
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <span className="text-muted text-sm">
                Page {page} &middot; {data.total} total
              </span>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={data.users.length < 50}
              >
                Next
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
